/**
 * Bütünsel ekran veri durumu — staleness banner'ı için zenginleştirilmiş sinyal.
 *
 * Faz 1.18 K1 + K3: tek başına `globalStaleness` "hiç tick yok" ve "önbellekten
 * gösteriliyor" durumlarını ayırt edemiyordu → sessiz boş/bayat ekran. Burada
 * canlı-doğrulama (clock-sync), tick varlığı ve layout hazır-olma süresi birleştirilir.
 *
 *   - liveVerified = clockSync.samples >= 1  → CANLI gateway'den server_ts geldi.
 *     (Dexie önbelleğinden yüklenen tick'ler observe() çağırmaz → cache, liveVerified'i tetiklemez.)
 *
 * Memory: decision_pwa_resilience.md §1/§3 + faz-1.18-pwa-ux-denetimi.md
 */
import { clockSync } from './clock-sync.js';
import { connectionState } from './connection-state.js';
import { tickStore } from '../renderer/tick-store.js';
import { globalStaleness } from './staleness.js';
import { collectorsAlive } from './source-status.js';

let layoutReadyAt: number | null = null;

/** İlk layout (cache veya canlı) ekrana basıldığında çağrılır — idempotent. */
export function markLayoutReady(): void {
    if (layoutReadyAt === null) layoutReadyAt = Date.now();
}

/** "Fiyat bekleniyor" uyarısı için bekleme payı — layout hazır olduktan sonra. */
const AWAIT_GRACE_MS = 30_000;

export type ScreenStatusKind =
    | 'ok' // canlı doğrulanmış + taze (banner gizli)
    | 'cache' // önbellekten gösteriliyor, henüz canlı doğrulama yok
    | 'awaiting' // layout var ama hiç fiyat akmıyor (uzun süredir)
    | 'calm' // bağlı + collector canlı ama fiyat sabit (sakin/kapalı piyasa) — alarm DEĞİL
    | 'critical'; // veri akışı GERÇEKTEN kesildi / çok bayat (bağlantı kopuk veya collector ölü)

export interface ScreenStatus {
    kind: ScreenStatusKind;
    ageLabel: string;
}

export function computeScreenStatus(): ScreenStatus {
    // Henüz hiç ekran basılmadıysa: bağlantı bandı durumu üstlenir, sessiz kal.
    if (layoutReadyAt === null) return { kind: 'ok', ageLabel: '' };

    const liveVerified = clockSync.samples >= 1;
    const hasAnyTick = tickStore.size() > 0;
    const sinceLayout = Date.now() - layoutReadyAt;
    const online = connectionState.state === 'online';

    if (!liveVerified) {
        // Canlı gateway ile henüz temas yok.
        if (hasAnyTick) {
            // Dexie önbelleğinden gösteriyoruz — güncel olduğu GARANTİ değil (K3).
            return { kind: 'cache', ageLabel: '' };
        }
        // Cache de yok: layout geldi ama hiç veri yok. Bağlantı kopuksa connection-banner
        // zaten bilgilendiriyor — çift uyarı yapma.
        if (online && sinceLayout > AWAIT_GRACE_MS) {
            return { kind: 'awaiting', ageLabel: '' };
        }
        return { kind: 'ok', ageLabel: '' };
    }

    // Canlı doğrulandı — gerçek staleness geçerli.
    if (!hasAnyTick) {
        // Layout var, gateway canlı ama HİÇ fiyat akmıyor (collector down / sembol yok) (K1).
        if (sinceLayout > AWAIT_GRACE_MS) {
            return { kind: 'awaiting', ageLabel: '' };
        }
        return { kind: 'ok', ageLabel: '' };
    }

    const g = globalStaleness(tickStore.allServerTimestamps());
    if (g.level === 'critical') {
        // Çok bayat (>300sn). AMA bağlantı sağlam + collector canlıysa bu "akış kesildi"
        // DEĞİL → fiyat hareket etmiyor (hafta sonu/sakin/kapalı piyasa). Kırmızı alarm yerine
        // nötr 'calm'. Yalnız bağlantı kopuk VEYA collector ölü ise gerçek 'critical' alarm.
        if (online && collectorsAlive()) {
            return { kind: 'calm', ageLabel: g.ageLabel };
        }
        return { kind: 'critical', ageLabel: g.ageLabel };
    }
    return { kind: 'ok', ageLabel: g.ageLabel };
}
